import { DateTime } from 'luxon';

import type { Receipt } from 'app-components/flux/receipts';
import { transactionsTypes } from 'app-components/flux/transactions';
import type {
  Attachment,
  CashFlowTransaction,
  TransactionProps,
} from 'app-components/flux/transactions/transactionsTypes';

/**
 * Transaction writes can strip `source` from the receipt-link attachment (the write schema treats
 * sourceless attachments as STORAGE), which makes the receipt attachment render as an empty
 * document chip and hides the receipt section. The receipts service still holds the authoritative
 * link, so when the linked receipt is known, restore RECEIPT on the attachment that references it.
 */
export const repairReceiptAttachmentSources = (
  attachments: Attachment[] | null | undefined,
  receipt: Receipt | undefined,
): Attachment[] | null | undefined => {
  if (!receipt || !attachments?.length) {
    return attachments;
  }

  let repaired = false;
  const next = attachments.map((attachment) => {
    if (attachment.source == null && (attachment.id === receipt.id || attachment.id === receipt.documentId)) {
      repaired = true;
      return { ...attachment, source: transactionsTypes.AttachmentSource.Receipt } as Attachment;
    }
    return attachment;
  });

  return repaired ? next : attachments;
};

const normalizeReceiptDate = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = DateTime.fromISO(value, { zone: 'utc' });
  return parsed.isValid ? (parsed.toISODate() ?? undefined) : undefined;
};

export const buildReceiptTransaction = (receipt: Receipt): CashFlowTransaction => {
  const { extraction, memo } = receipt;
  const postedOn = normalizeReceiptDate(extraction?.transactionDate);
  const prefill: Partial<TransactionProps> = {
    ...(extraction?.total !== undefined ? { amount: -extraction.total } : {}),
    ...(memo !== undefined ? { memo } : {}),
    ...(extraction?.merchant?.trim() ? { payee: extraction.merchant.trim() } : {}),
    ...(postedOn ? { postedOn } : {}),
  };

  return transactionsTypes.mkCashFlowTransaction(prefill);
};
